import { type QueryFunctionContext } from '@tanstack/react-query'
import { cleanFlags } from '@/features/shared/services/FeatureFlags/utils'
import {
  defaultFlags,
  defaultFlagsWithParams,
} from '@/features/shared/services/FeatureFlags/constants'
import { apiGet } from '@/features/shared/utils/dataFetching'
import {
  type FeatureFlagsKey,
  type FeatureFlagsWithParamsKey,
} from '@/features/shared/services/FeatureFlags/queries'
import { type HttpFeatureFlagsResponse } from '@/features/shared/services/FeatureFlags/types'

const fetchFlags = async <TKey extends string>({
  context,
  userId,
  params,
  defaultsFlags,
  fetcherName,
}: {
  context: QueryFunctionContext
  userId: number
  params: { Store: number; Location: number; Metro: number }
  defaultsFlags: Record<TKey, boolean>
  fetcherName: string
}): Promise<Record<TKey, boolean>> => {
  try {
    const data = await apiGet<HttpFeatureFlagsResponse>({
      config: {
        url: 'feature-flags/v1/flagsWithContext',
        params: { ServiceName: 'Segway', ...params, User: userId },
      },
      options: { includeAuthHeader: Boolean(userId) },
      fetcherName,
      context,
    })
    return cleanFlags({ data, defaultsFlags })
  } catch {
    return cleanFlags({ data: undefined, defaultsFlags })
  }
}

export const fetchFeatureFlags = (context: QueryFunctionContext<FeatureFlagsKey>) => {
  const [, userId] = context.queryKey
  return fetchFlags({
    context,
    userId,
    params: { Store: 0, Location: 0, Metro: 0 },
    defaultsFlags: defaultFlags,
    fetcherName: 'fetchFeatureFlags',
  })
}

export const fetchFeatureFlagsWithParams = (
  context: QueryFunctionContext<FeatureFlagsWithParamsKey>,
) => {
  const [, userId, params] = context.queryKey
  return fetchFlags({
    context,
    userId,
    params: {
      Store: params.store_id ?? 0,
      Location: params.store_location_id ?? 0,
      Metro: params.metro_id ?? 0,
    },
    defaultsFlags: defaultFlagsWithParams,
    fetcherName: 'fetchFeatureFlagsWithParams',
  })
}
